/**
 * 80mm rolls clip the physical right edge. Extra padding on the right
 * (and a smaller pad on the left) keeps text inside the printable area.
 * Order is CSS shorthand: top, right, bottom, left.
 */
export const THERMAL_PRINT_PADDING = '1mm 5mm 1mm 2mm';

export const BILL_SIZES = {
  A4: {
    width: '210mm',
    minHeight: '297mm',
    height: '297mm',
    pageSize: 'A4 portrait',
    orientation: 'portrait',
  },
  // Half of A4: 148mm × 210mm portrait (A4 cut in half, standing).
  A5: {
    width: '148mm',
    minHeight: '210mm',
    height: '210mm',
    pageSize: 'A5 portrait',
    orientation: 'portrait',
  },
  // 80mm thermal receipt roll.
  Thermal: {
    width: '80mm',
    minHeight: '120mm',
    height: 'auto',
    pageSize: '80mm auto',
    orientation: 'portrait',
  },
};

export function getBillDimensions(size = 'A4') {
  return BILL_SIZES[size] || BILL_SIZES.A4;
}

export function isThermalBill(pageSize = 'A4') {
  return pageSize === 'Thermal';
}

export function isCompactBill(pageSize = 'A4') {
  return pageSize === 'A5' || pageSize === 'Thermal';
}

/** Spacing/type tokens so A5 / Thermal fit header + rows + totals + footer. */
export function getBillLayout(pageSize = 'A4') {
  const thermal = isThermalBill(pageSize);
  const compact = isCompactBill(pageSize);
  return {
    compact,
    thermal,
    headerHeight: thermal ? 44 : compact ? 52 : 100,
    logoSize: thermal ? 28 : compact ? 34 : 80,
    logoBorder: thermal ? 1 : compact ? 2 : 3,
    titleFontSize: thermal ? 11 : compact ? 14 : 26,
    billLabelFontSize: thermal ? 8 : compact ? 10 : 18,
    billLabelPadding: thermal ? '2px 6px' : compact ? '3px 8px' : '8px 20px',
    serialFontSize: thermal ? 8 : compact ? 10 : 16,
    headerPadding: thermal ? '3px' : compact ? '4px' : '10px',
    infoPadding: thermal ? '4px 6px' : compact ? '5px 8px' : '15px 20px',
    infoFontSize: thermal ? 8 : compact ? 9 : 14,
    infoMarginBottom: thermal ? 1 : compact ? 2 : 5,
    tableWrapPadding: thermal ? '0 4px' : compact ? '0 8px' : '0 20px',
    tableMarginTop: thermal ? 3 : compact ? 4 : 10,
    thPadding: thermal ? '2px 1px' : compact ? '3px 2px' : '8px',
    tdPadding: thermal ? '1px 1px' : compact ? '2px 2px' : '10px',
    thFontSize: thermal ? 7 : compact ? 8 : 13,
    tdFontSize: thermal ? 7 : compact ? 8 : 13,
    indexFontSize: thermal ? 7 : compact ? 8 : 14,
    colIndex: thermal ? 16 : compact ? 22 : 60,
    colSize: thermal ? 28 : compact ? 40 : 100,
    colDesign: thermal ? 28 : compact ? 40 : 90,
    colQty: thermal ? 22 : compact ? 28 : 80,
    colPrice: thermal ? 32 : compact ? 42 : 100,
    colTotal: thermal ? 36 : compact ? 50 : 120,
    totalsPadding: thermal ? '4px 6px' : compact ? '6px 8px' : '20px',
    totalsBoxPadding: thermal ? '3px 6px' : compact ? '4px 10px' : '15px 40px',
    totalsTitleSize: thermal ? 8 : compact ? 10 : 18,
    totalsValueSize: thermal ? 10 : compact ? 12 : 20,
    totalsBoxMinWidth: thermal ? 72 : compact ? 96 : 200,
    paidFontSize: thermal ? 8 : compact ? 9 : 14,
    signatureMarginTop: thermal ? 4 : compact ? 8 : 30,
    signatureLineMargin: thermal ? 6 : compact ? 10 : 30,
    signatureWidth: thermal ? 70 : compact ? 90 : 150,
    footerPadding: thermal ? '3px 4px' : compact ? '5px 8px' : '12px 20px',
    footerFontSize: thermal ? 6 : compact ? 7 : 12,
    footerAddressSize: thermal ? 5.5 : compact ? 6.5 : 11,
    footerMarginTop: thermal ? 2 : compact ? 2 : 20,
    footerAddressMaxWidth: thermal ? 120 : compact ? 180 : 400,
    footerAddressPadding: thermal ? '2px 8px 2px 6px' : compact ? '3px 10px 3px 8px' : '8px 30px 8px 15px',
  };
}

export function getBillContainerStyle(pageSize = 'A4') {
  const dims = getBillDimensions(pageSize);
  const compact = isCompactBill(pageSize);
  const thermal = isThermalBill(pageSize);
  return {
    width: dims.width,
    minHeight: dims.minHeight,
    height: thermal ? 'auto' : dims.height,
    maxHeight: compact && !thermal ? dims.height : undefined,
    overflow: compact && !thermal ? 'hidden' : undefined,
    boxSizing: 'border-box',
    padding: thermal ? THERMAL_PRINT_PADDING : 0,
  };
}

function getPrintPageCss(dims, pageSize = 'A4') {
  const thermal = isThermalBill(pageSize);
  return `
    @media print {
      @page { size: ${dims.pageSize}; margin: 0; }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: ${dims.width} !important;
        ${thermal ? 'height: auto !important;' : `height: ${dims.height} !important;`}
        overflow: ${thermal ? 'visible' : 'hidden'} !important;
      }
      body * { visibility: hidden; }
      .printable-bill, .printable-bill * { visibility: visible; }
      .printable-bill {
        position: absolute;
        left: 0;
        top: 0;
        width: ${dims.width} !important;
        max-width: ${dims.width} !important;
        min-height: ${dims.minHeight} !important;
        ${thermal ? 'height: auto !important; max-height: none !important; overflow: visible !important;' : `height: ${dims.height} !important; max-height: ${dims.height} !important; overflow: hidden !important;`}
        box-sizing: border-box !important;
        padding: ${thermal ? THERMAL_PRINT_PADDING : '0'} !important;
        background: white !important;
        box-shadow: none !important;
        page-break-after: avoid !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      button, .no-print { display: none !important; }
    }
  `;
}

/** Print a bill ref with A4, A5, or Thermal page size (keeps on-screen preview size in sync). */
export function printBillFromRef(billRef, pageSize = 'A4') {
  if (!billRef?.current) return;
  const dims = getBillDimensions(pageSize);
  const styleId = 'dynamic-bill-print-style';
  let styleEl = document.getElementById(styleId);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = getPrintPageCss(dims, pageSize);
  const printContents = billRef.current.innerHTML;
  const originalContents = document.body.innerHTML;
  document.body.innerHTML = `<div class="printable-bill">${printContents}</div>`;
  window.print();
  document.body.innerHTML = originalContents;
  window.location.reload();
}

export function getBillPrintCss(pageSize = 'A4') {
  return getPrintPageCss(getBillDimensions(pageSize), pageSize);
}
